// A league's divisions (PLAN-2026-10-29-divisions, scenario B4): how many a
// league may have, the names they start with, and how the teams are split
// between them. One copy, read by the server that writes the split and by every
// client that draws the editor.

import type { LeagueSettings } from './schemas';

export type Division = LeagueSettings['schedule']['divisions'][number];

/** At most four, as ESPN allows; one would be the whole league. */
export const MAX_DIVISIONS = 4;

/**
 * THE NAMES A LEAGUE STARTS WITH, by how many it has: compass names, which
 * belong to no sport, and every one can be changed.
 */
const STARTING_NAMES: Record<number, readonly string[]> = {
  2: ['East', 'West'],
  3: ['East', 'Central', 'West'],
  4: ['North', 'South', 'East', 'West'],
};

/** The counts a league of `teamCount` teams may choose: none, or two up to one a pair of teams. */
export function divisionCounts(teamCount: number): number[] {
  const most = Math.min(MAX_DIVISIONS, Math.floor(teamCount / 2));
  return [0, ...Array.from({ length: Math.max(0, most - 1) }, (_, i) => i + 2)];
}

/**
 * `count` divisions, keeping the first of `existing` as they are (a rename
 * survives a change of count) and naming each one added with the first of its
 * count's names not already taken. Ids are `div-1`, `div-2`… and never reused
 * by a division added beside the ones kept.
 */
export function divisionsFor(count: number, existing: readonly Division[]): Division[] {
  if (count <= 0) return [];
  const out = existing.slice(0, count).map((d) => ({ ...d }));
  const taken = (name: string) =>
    out.some((d) => d.name.trim().toLowerCase() === name.toLowerCase());
  const ids = new Set(existing.map((d) => d.id));
  let next = 1;
  const freshId = () => {
    while (ids.has(`div-${next}`)) next += 1;
    ids.add(`div-${next}`);
    return `div-${next}`;
  };
  for (let n = 1; out.length < count; n += 1) {
    const name =
      (STARTING_NAMES[count] ?? []).find((x) => !taken(x)) ??
      (taken(`Division ${n}`) ? null : `Division ${n}`);
    if (name !== null) out.push({ id: freshId(), name });
  }
  return out;
}

/** Whether two lists name the same divisions, whatever they are called. */
export function sameDivisions(a: readonly Division[], b: readonly Division[]): boolean {
  const ids = (x: readonly Division[]) => [...new Set(x.map((d) => d.id))].sort().join('|');
  return ids(a) === ids(b);
}

/**
 * EVERY TEAM IN A DIVISION, split evenly by seat (B-561): the first seat in
 * the first division, the second in the second, and round again. `teamIds` is
 * in seat order; with no divisions every team is in none.
 */
export function splitEvenly(
  teamIds: readonly string[],
  divisions: readonly Division[],
): Map<string, string | null> {
  return new Map(
    teamIds.map((id, i) => [
      id,
      divisions.length === 0 ? null : divisions[i % divisions.length]!.id,
    ]),
  );
}
